import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { authAPI } from '../services/api'
import { parseOAuthCallback } from '../../shared/oauth'

export default function OAuthCallbackPage() {
  const [error, setError] = useState('')
  const setUser = useAuthStore((state) => state.setUser)
  const navigate = useNavigate()
  const hasProcessed = useRef(false)

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double execution in React StrictMode
      if (hasProcessed.current) return
      hasProcessed.current = true

      try {
        const { code, error: oauthError } = parseOAuthCallback(window.location.href)

        if (oauthError) {
          setError(`OAuth error: ${oauthError}`)
          setTimeout(() => navigate('/login'), 3000)
          return
        }

        if (!code) {
          setError('No authorization code received')
          setTimeout(() => navigate('/login'), 3000)
          return
        }

        // Exchange code for tokens
        const redirectUri = window.location.origin + '/auth/callback'
        await authAPI.exchangeOAuthCode(code, redirectUri)

        // Token stored in HTTP-only cookie by server
        // Fetch user info from /me endpoint
        const userInfo = await authAPI.getMe()

        setUser({
          email: userInfo.attributes.email,
          name: userInfo.attributes.name || userInfo.attributes.given_name || userInfo.username,
          sub: userInfo.attributes.sub,
        })

        navigate('/')
      } catch (err: any) {
        console.error('OAuth callback error:', err)
        setError(err.message || 'OAuth signin failed')
        setTimeout(() => navigate('/login'), 3000)
      }
    }

    handleCallback()
  }, [navigate, setUser])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
        {error ? (
          <>
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
            <p className="text-slate-700 mb-4">{error}</p>
            <p className="text-sm text-slate-500">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Signing you in...</h2>
            <p className="text-slate-600">Please wait</p>
          </>
        )}
      </div>
    </div>
  )
}
